import React, { useEffect, useState, useCallback } from "react";
import NavigateBack from "../../components/navigateBack";
import { Container, Grid, makeStyles, Typography } from "@material-ui/core";

import createPersistedState from "use-persisted-state";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import Rapid3Assessment from "./rapid3Assessment";

const useTokenState = createPersistedState("token");
const usePIDState = createPersistedState("pid");

const RheumatoidArthritisAssessment = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [currentAppointment, setCurrentAppointment] = useState(null);
  const [currentRapid3Info, setCurrentRapid3Info] = useState(null);

  const [tab, setTab] = useState(0);
  const [error, setError] = useState(null);

  const history = useHistory();
  const [token] = useTokenState();
  const [pid] = usePIDState();

  const useStyles = makeStyles((theme) => ({
    selectedTab: {
      backgroundColor: "#0e426a",
      height: 50,
      color: "#ffffff",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
    },
    unselectedTab: {
      backgroundColor: "#97d1fa57",
      height: 50,
      color: "#0060a6b3",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
    },
    disabled: {
      pointerEvents: "none",
      opacity: 0.5,
    },
    errorMessage: {
      color: "#d32f2f",
      textAlign: "center",
      padding: theme.spacing(2),
      marginTop: "78px",
    },
  }));
  const classes = useStyles();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const assessment = queryParams.get("assessment");

    if (assessment === "RAPID3") {
      setTab(0);
    }
  }, [location.search]);

  // Centralized error and auth handling
  const handleApiError = useCallback(
    (error, responseData) => {
      if (responseData?.status?.code === 4011) {
        localStorage.clear();
        history.push("/login");
        return;
      }

      console.error("API Error:", error);
      setError(error.message || "An error occurred");
    },
    [history]
  );

  // Fetch rapid3 score and details
  const fetchRapid3PainScore = useCallback(async () => {
    if (!pid || !token) return null;

    try {
      const response = await fetch(`/api/v1/pain-score/rapid3/${pid}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = await response.json();

      if (responseData?.status?.success) {
        setCurrentRapid3Info(responseData.current);
        return responseData.current;
      } else {
        handleApiError(new Error("Failed to fetch rapid3 score"), responseData);
        return null;
      }
    } catch (error) {
      handleApiError(error);
      return null;
    }
  }, [pid, token, handleApiError]);

  // Appointment fetching
  const fetchAppointmentInfo = useCallback(async () => {
    if (!pid || !token) return null;

    try {
      const response = await fetch(`/api/v1/appointments?attendeeId=${pid}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = await response.json();

      if (responseData?.status?.success) {
        setCurrentAppointment(responseData.appointment);
        return responseData.appointment;
      } else {
        handleApiError(new Error("Failed to fetch appointment"), responseData);
        return null;
      }
    } catch (error) {
      handleApiError(error);
      return null;
    }
  }, [pid, token, handleApiError]);

  // Initialize data on mount
  useEffect(() => {
    const initializeData = async () => {
      if (!pid || !token) {
        history.push("/login");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        await Promise.all([fetchAppointmentInfo(), fetchRapid3PainScore()]);
      } catch (error) {
        setError("Failed to load initial data");
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [pid, token, fetchAppointmentInfo, fetchRapid3PainScore, history]);

  // Callback to update Rapid3 info from child component
  const handleRapid3InfoUpdate = useCallback((newRapid3Info) => {
    setCurrentRapid3Info(newRapid3Info);
  }, []);

  if (isLoading) {
    return (
      <>
        <NavigateBack title="Rheumatoid Arthritis" />
        <div className="body_content">
          <div className="inner_body py-5">
            <div
              style={{
                margin: "auto",
                display: "flex",
                justifyContent: "center",
              }}
              id="loader"
            />
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <NavigateBack title="Rheumatoid Arthritis" />
        <div className={classes.errorMessage}>
          <Typography variant="h6">Error: {error}</Typography>
          <button
            onClick={() => window.location.reload()}
            className="btn btn_primary mt-3"
          >
            Retry
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <NavigateBack title="Rheumatoid Arthritis" />
      <div style={{ marginTop: "75px" }}>
        <Grid container spacing={0}>
          {/* Tab Navigation */}
          <Grid item xs={12}>
            <Container maxWidth="sm" disableGutters onClick={() => setTab(0)}>
              <Typography
                component="div"
                className={
                  tab === 0 ? classes.selectedTab : classes.unselectedTab
                }
              >
                RAPID3
              </Typography>
            </Container>
          </Grid>

          {/* Tab Content */}
          {tab === 0 && currentAppointment && (
            <Grid item xs={12}>
              <Container maxWidth="sm" disableGutters>
                <Rapid3Assessment
                  appointmentId={currentAppointment.id}
                  pid={pid}
                  token={token}
                  currentRapid3Info={currentRapid3Info}
                  onRapid3InfoUpdate={handleRapid3InfoUpdate}
                  onError={handleApiError}
                />
              </Container>
            </Grid>
          )}
        </Grid>
      </div>
    </>
  );
};

export default RheumatoidArthritisAssessment;
